import React, { useEffect, useMemo, useState } from "react";
import "./editPatient.scss";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import GenericForm, { FormField } from "../../../components/GenericForm";
import {
  getAllHCPs,
  getPatientsTreatments,
  updatePatient,
} from "../../../common/api/apiCalls";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../common/contexts/AlertContext";
import { GenderEnum } from "../../../common/types/gederEnum";
import Table from "../../../components/patientsList/genericTable/Table";
import { DateRange } from "react-day-picker";
import { organizePatientTreatments } from "../../../common/function/organizePatientTreatments";
import Loader from "../../../components/Loader/Loader";

const sortKeyArray = [
  "Treatment_Date",
  "Treatment_Start_Time",
  "Treatment_End_Time",
  "Reports_Table",
  "Charts",
];

const EditPatient = () => {
  
  const location = useLocation();

  const user = location.state?.user; // Access the user data from state
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();

  const [filters, setFilters] = useState<string[]>([]);
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    undefined
  );
  const [sortKey, setSortKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");

  const { isLoading, error, data } = useQuery({
    queryKey: ["treatmentData", user, selectedRange, sortKey, sortOrder],
    queryFn: () =>
      getPatientsTreatments({
        sortKey,
        sortOrder,
        creationDateRange: selectedRange,
        patientId: user.id,
      }),
  });

  const {
    isLoading: isHcpLoading,
    error: hcpError,
    data: hcpData,
  } = useQuery({
    queryKey: ["hcpData", user],
    queryFn: getAllHCPs,
  });
  // Define the form fields and set initial values based on the user data
  let initialFields: FormField[] = [
    {
      label: "First Name",
      name: "firstName",
      type: "text",
      initialValue: user?.firstName || "",
    },
    {
      label: "Last Name",
      name: "lastName",
      type: "text",
      initialValue: user?.lastName || "",
    },
    {
      label: "Email",
      name: "email",
      type: "email",
      initialValue: user?.email || "",
      disabled: true,
    },
    {
      label: "ID Number",
      name: "idNumber",
      type: "text",
      initialValue: user?.identityNumber || "",
    },
    {
      label: "Social Security Number",
      name: "socialSecurityNumber",
      type: "text",
      initialValue: user?.socialSecurityNumber || "",
    },
    {
      label: "Phone Number*",
      name: "phoneNumber",
      type: "phoneInput",
      initialValue: user?.phoneNumber || "",
    },
    {
      label: "Gender*",
      name: "gender",
      type: "select",
      options: [
        { label: GenderEnum.Male, value: GenderEnum.Male },
        { label: GenderEnum.Female, value: GenderEnum.Female },
      ],
      initialValue: user?.gender,
    }, // No options initially
    {
      label: "Related HCP*",
      name: "relatedHCP",
      type: "select",
      options: [],
      initialValue: user?.relatedHCP || "",
    }, // No options initially
  ];
  const [fields, setFields] = useState<FormField[]>(initialFields);
  const getText = (text: string) => t(`CreateUser.${text}`);
  const [showModal, setShowModal] = useState(false);
  const organizedData = useMemo(() => organizePatientTreatments(data), [data]);

  const handleSubmit = async (formData: { [key: string]: any }) => {
    console.log("Form submitted with data:", formData);

    const res = await updatePatient({
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      identityNumber: formData.idNumber,
      phoneNumber: formData.phoneNumber,
      gender: formData.gender,
      relatedHCP: formData.relatedHCP,
      socialSecurityNumber: formData.socialSecurityNumber,
    });

    if (res.success) {
      alert("Success", "msg");
      navigate(-1);
    } else {
      alert(res.err, "error");
    }
  };
  useEffect(() => {
    if (Array.isArray(hcpData)) {
      const updatedFields = fields.map((field) => {
        if (field.name === "relatedHCP") {
          return {
            ...field,
            options: hcpData.map((HCP: any) => ({
              label: HCP.name,
              value: HCP.username,
            })),
          };
        }
        return field;
      });
      const id = updatedFields.find((val) => val.label === "ID Number");
      const ssn = updatedFields.findIndex(
        (val) => val.label === "Social Security Number"
      );

      if (id && !id.initialValue) {
        setFields(updatedFields);
        return;
      }

      const updatedFieldsWithoutSSN = updatedFields.filter(
        (field) => field.name !== "socialSecurityNumber"
      );
      setFields(updatedFieldsWithoutSSN);
    }
  }, [hcpData]);

  if (isLoading|| isHcpLoading) {
    return  <Loader text={getText("loading")} />;
  }

  if (error|| hcpError) {
    return <div>{getText("Error fetching treatments")}</div>;
  }

  return (
    <>
      <GenericForm
        backButtonAction={() => navigate(-1)}
        backButtonText={getText("Back")}
        buttonText={getText("Save_changes")}
        fields={fields}
        onSubmit={handleSubmit}
        title={getText("Patient_Profile")}
      >
        <div className="main-table-container">
          <h4 className="title">{getText("Treatment_History")}</h4>
          <div className="table-container">
            <Table
              dateFilter={{ selectedRange, setSelectedRange }}
              rowClick={() => {}}
              filterObj={{}}
              setSortOrder={setSortOrder}
              sortOrder={sortOrder}
              tableHeadline={"tableHeadline"}
              trashAction={() => {}}
              setFilters={() => {}}
              // filters={filters}
              buttonText={"buttonText"}
              // buttonAction={"buttonAction"}
              // tableData={[]}
              tableData={organizedData}
              setSortKey={setSortKey}
              sortKey={sortKey}
              sortKeysArray={sortKeyArray.map((value) => {
                return { name: getText(`sort.${value}`), val: value };
              })}
              showCheckbox={false}
              // EndRowIcon={svgs.icons.Eye}
            />
          </div>
        </div>
      </GenericForm>
    </>
  );
};

export default EditPatient;
